/**
 * The draggable battle-cam PIP (portrait, r141; owner: "snap + remember"). A press on the PIP that moves past
 * the slop drags a ghost outline with the finger (or mouse); the release snaps the PIP to the map corner
 * nearest the ghost's centre, and a short press stays a tap (follow the chaser). The corner is UI state, not in
 * the deep link, remembered per race in localStorage.
 */
import type { Rect } from '../core/api.ts';
import type { Corner } from '../core/hud/north-arrow.ts';
import type { StageLayout } from './stage.ts';

const KEY = 'undercut.pip.';
/** CSS px a press moves before it counts as a drag (the sheet's slop) */
const DRAG_SLOP = 8;

/** The map quadrant of a stage point. */
export function cornerAt(x: number, y: number, map: Rect): Corner {
  const left = x < map.x + map.w / 2;
  return y < map.y + map.h / 2 ? (left ? 'tl' : 'tr') : left ? 'bl' : 'br';
}

/** The remembered corner for a race; storage can be blocked, then the race preset's corner. */
export function readPipCorner(race: string): Corner | null {
  try {
    const v = localStorage.getItem(KEY + race);
    return v === 'tl' || v === 'tr' || v === 'bl' || v === 'br' ? v : null;
  } catch {
    return null;
  }
}

export function writePipCorner(race: string, c: Corner): void {
  try {
    localStorage.setItem(KEY + race, c);
  } catch {
    // storage blocked: the corner lasts for this page only
  }
}

export class PipDrag {
  /** the ghost outline that follows the pointer while dragging */
  readonly el: HTMLDivElement;
  private press: { id: number; x: number; y: number; pip: Rect; map: Rect; css: number; drag: boolean } | null = null;

  constructor(private readonly onDrop: (c: Corner) => void) {
    this.el = document.createElement('div');
    this.el.id = 'pip-ghost';
    this.el.hidden = true;
  }

  /** A press on the PIP (`pip`, `map` in stage px). */
  down(id: number, clientX: number, clientY: number, pip: Rect, map: Rect, l: StageLayout): void {
    if (this.press) return;   // one finger drags; a second press on the PIP is ignored
    const css = l.resolved.f / l.dpr;
    this.press = { id, x: clientX, y: clientY, pip, map, css, drag: false };
    const s = this.el.style;
    s.left = `${String(l.resolved.offset.x / l.dpr + pip.x * css)}px`;
    s.top = `${String(l.resolved.offset.y / l.dpr + pip.y * css)}px`;
    s.width = `${String(pip.w * css)}px`;
    s.height = `${String(pip.h * css)}px`;
  }

  /** A pointer move; true while this pointer drags the PIP (the caller drops its pending tap). */
  move(id: number, clientX: number, clientY: number): boolean {
    const p = this.press;
    if (!p || p.id !== id) return false;
    const dx = clientX - p.x, dy = clientY - p.y;
    if (!p.drag && Math.hypot(dx, dy) > DRAG_SLOP) p.drag = true;
    if (p.drag) {
      this.el.hidden = false;
      this.el.style.transform = `translate(${String(dx)}px, ${String(dy)}px)`;
    }
    return p.drag;
  }

  /** The press ends; true when it was a drag (no tap). A drag that is not cancelled snaps the PIP. */
  up(id: number, clientX: number, clientY: number, cancel: boolean): boolean {
    const p = this.press;
    if (!p || p.id !== id) return false;
    this.press = null;
    this.el.hidden = true;
    this.el.style.transform = '';
    if (p.drag && !cancel) {
      const cx = p.pip.x + p.pip.w / 2 + (clientX - p.x) / p.css;
      const cy = p.pip.y + p.pip.h / 2 + (clientY - p.y) / p.css;
      this.onDrop(cornerAt(cx, cy, p.map));
    }
    return p.drag;
  }
}
